import React, { useMemo } from 'react';
import './MeterReadingEventsHistory.less';
import { MeterRateTaskType, ReadingEventType } from '../../../../redux/jobs/jobs.types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { resolveReadableRateNameWithObis, resolveUnitByObis } from '../../../../helper/MeterHelper';
import { evaluationSelectors } from '../../../../redux/evaluation';
import { useTranslation } from 'react-i18next';
import Collapsable from '../../../../components/envago/Collapsable/Collapsable';
import { groupBy } from '../../../../helper/GroupHelper';
import dayjs from 'dayjs';
import { formatDate } from '../../../../helper/DateHelper';
import { formatNumberToLocaleString, insertArithmeticOperator, splitJoin } from '../../../../helper/TextHelper';
import Icon from '../../../../components/envago/Icon/Icon';
import { resolveReadingEventsConsumptions } from '../../../../helper/ConsumptionHelper';
import ReadingValueComposedChart from '../../../../components/envago/ReadingValueComposedChart';
import { roundNumberToLocaleString } from '../../../../helper/NumberHelper';
import { applicationSelectors } from '../../../../redux/application';
import JobMeterRateTaskReadingImages, { MeterReadingPreviewImage } from '../../../JobsScreen/components/JobDetails/components/JobMeterRateTaskReadingImages';

interface IMeterReadingEventsHistoryProps {
    task: MeterRateTaskType;
    readingResultImages?: Array<MeterReadingPreviewImage>;
    className?: string;
    defaultOpen?: boolean;
}

const MeterReadingEventsHistory = ({ task, className, defaultOpen = false, readingResultImages }: IMeterReadingEventsHistoryProps) => {
    const { t } = useTranslation();
    const readings = useSelector(evaluationSelectors.getUnifiedReadingsForTask(task, readingResultImages));
    const evaluationIgnoreEstimatedReadingIfOtherReadingIsWithinDays = useSelector(
        applicationSelectors.evaluationIgnoreEstimatedReadingIfOtherReadingIsWithinDays,
    );

    const readableRateName = resolveReadableRateNameWithObis(task);
    const groupedEvaluatedReadingsByYear = useMemo(() => {
        const readingsEventsWithConsumptions = resolveReadingEventsConsumptions(readings, evaluationIgnoreEstimatedReadingIfOtherReadingIsWithinDays);

        const readingsByYear = groupBy(readingsEventsWithConsumptions, 'readingDate', (readingDate) => dayjs(readingDate).format('YYYY'));

        return Object.entries(readingsByYear).sort(([a], [b]) => b.localeCompare(a));
    }, [readings, evaluationIgnoreEstimatedReadingIfOtherReadingIsWithinDays]);

    const chartData = useMemo(
        () =>
            readings.map((event) => {
                return {
                    readingDate: dayjs(event.readingDate).valueOf(),
                    readingValue: event.readingValue,
                };
            }),
        [readings],
    );

    const resolveReadableReason = (event: ReadingEventType) => {
        if (event.reason === 'OTHER') {
            return event.otherReason
                ? t([`common.reasons.${event.otherReason}`, `${event.otherReason}`]) // with fallback
                : t(`common.reasons.${event.reason}`);
        } else {
            return t(`common.reasons.${event.reason}`);
        }
    };

    if (groupedEvaluatedReadingsByYear.length === 0) {
        return null;
    }

    return (
        <>
            <Collapsable
                defaultOpen={defaultOpen}
                className={classNames('meter-reading-events-history mb-4 bg-gray-100 shadow-md p-2 rounded', className)}
                arrowPosition={'right'}
                triggerText={
                    <div className={'text-lg font-medium h-6 flex justify-between items-center mr-2'}>
                        <div className={'self-center'}>{readableRateName || 'Ablesehistorie'}</div>
                        {readings.length > 1 && (
                            <div aria-hidden={true} className={'evaluation-chart self-center'}>
                                <ReadingValueComposedChart data={chartData} />
                            </div>
                        )}
                    </div>
                }
            >
                {groupedEvaluatedReadingsByYear.map(([year, readingEvents], index) => (
                    <div key={index} className={'flex flex-col mb-4 ml-1 gap-y-2'}>
                        <div className={'evaluation-history-year text-xl font-bold text-gray-300'}>{year}</div>
                        <div className={'evaluation-history-events mx-4 flex flex-col gap-y-2'}>
                            {readingEvents.map((event, index) => {
                                const unit = task.obis && resolveUnitByObis(task.obis);
                                const readingValueReadable = formatNumberToLocaleString(event.readingValue, task.postDecimals);

                                const consumptionReadable = insertArithmeticOperator(formatNumberToLocaleString(event.consumption, task.postDecimals));

                                const consumptionPerDayReadable = roundNumberToLocaleString(event.consumptionPerDay);

                                return (
                                    <div key={index} className={'evaluation-history-event ml-2 pl-2 border-l border-solid border-gray-300'}>
                                        <div className={'text-sm font-medium xl:flex xl:justify-between'}>
                                            <div className={'flex flex-nowrap gap-x-4 ml-4 mb-1'}>
                                                <div className={'evaluation-history-event-date w-8'}>{formatDate(event.readingDate, 'DD.MM.')}</div>
                                                <div className={'evaluation-history-event-reason text-gray-400'}>{resolveReadableReason(event)}</div>
                                            </div>
                                            <div
                                                style={{
                                                    gridTemplateRows: 'min-content min-content 1fr'
                                                }}
                                                className={'grid grid-cols-12 grid-rows-3 w-80 xl:mr-0 items-stretch gap-x-1 ml-auto'}>
                                                <div className={'evaluation-history-event-value size-fit col-span-10 md:col-span-6 row-start-1 text-right'}>
                                                    {splitJoin(' ', readingValueReadable, unit)}
                                                </div>
                                                {event.readingImages && event.readingImages.length > 0 && (
                                                    <div className={'col-start-11 col-span-2 row-start-0 row-span-3'}>
                                                        <JobMeterRateTaskReadingImages
                                                            images={event.readingImages}
                                                            containerClassName={
                                                                // event.readingImages.length === 1
                                                                //     ? 'w-full h-full'
                                                                //     : 'grid grid-cols-2 grid-rows-2 gap-0.5 w-full h-full max-h-16'
                                                                "size-full"
                                                            }
                                                            imageSize={'auto'}
                                                            previewImageClassName={
                                                                    'evaluation-last-reading-result-image object-cover w-full h-full'
                                                            }
                                                        />
                                                    </div>
                                                )}

                                                {consumptionReadable && (
                                                    <>
                                                        <div
                                                            className={classNames(
                                                                'evaluation-history-event-value-readable col-start-1 col-span-10 10 md:col-span-6 row-start-2 mt-1 mb-2 text-xs font-light flex flex-nowrap gap-x-0.5 self-baseline justify-end',
                                                                {
                                                                    'text-gray-400': event.isGhost,
                                                                },
                                                            )}
                                                        >
                                                            <div>{consumptionReadable}</div>
                                                            {unit && <div>{unit}</div>}
                                                        </div>
                                                        {consumptionPerDayReadable && (
                                                            <div
                                                                className={
                                                                    'evaluation-history-event-daily-consumption col-start-7 col-span-4 row-start-2 text-xs font-light text-gray-400 flex flex-nowrap self-baseline gap-x-0.5 invisible md:visible '
                                                                }
                                                            >
                                                                <div>{`(`}</div>
                                                                <Icon
                                                                    className={'flex-shrink-0 self-center w-3'}
                                                                    aria={{ 'aria-label': 'Durchschnitt Symbol' }}
                                                                    path={Icon.Path.mdiDiameterVariant}
                                                                />
                                                                <div>{consumptionPerDayReadable}</div>
                                                                <div>{`/`}</div>
                                                                <div>{t('common.day')}</div>
                                                                <div>{`)`}</div>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </Collapsable>
        </>
    );
};

export default MeterReadingEventsHistory;
